import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import {ezprOrange} from './_helpers';
import {COLUMN_HEADER_CONFIG} from '../utils/columnsConfig.jsx';
import {Query, Transaction, BrickContext} from 'olympe';
import ColumnsToDisplay from '../models/cloudobjects/ColumnsToDisplay';
import UserSettings from '../models/cloudobjects/UserSettings';
import {useProperty} from '@olympeio/core';

/**
 * @param {ColumnsButton.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function ColumnsButton(props) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const userSettings = useProperty(props.context, 'User settings');

    const persistColumns = (selectedColumns) => {
        const ordered = COLUMN_HEADER_CONFIG
            .map((config) => config.name)
            .filter((name) => selectedColumns.includes(name));

        Query.instancesOf(ColumnsToDisplay)
            .execute()
            .then((instances) => {
                const transaction = Transaction.from(props.context);
                instances.forEach((model) => {
                    const name = model.getColumnName();
                    if (ordered.includes(name)) {
                        UserSettings.linkColumnToDisplay(transaction, userSettings, model);
                    } else {
                        UserSettings.unlinkColumnToDisplay(transaction, userSettings, model);
                    }
                });
                transaction.execute();
            })
            .catch((error) => {
                console.error('Error persisting columns:', error);
            });
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleToggleColumn = (name) => {
        const next = props.columnsToDisplay.includes(name) ?
            props.columnsToDisplay.filter((c) => c !== name) :
            [...props.columnsToDisplay, name];

        props.setColumnsToDisplay(next);
        persistColumns(next);
    };

    const handleReset = () => {
        const allColumns = COLUMN_HEADER_CONFIG.map((c) => c.name);
        props.setColumnsToDisplay(allColumns);
        persistColumns(allColumns);
    };

    return (
        <>
            <IconButton
                size="small"
                aria-controls={open ? 'columns-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                sx={{'&:hover': {color: ezprOrange}}}
            >
                <ViewColumnOutlinedIcon fontSize="medium" />
            </IconButton>

            <Menu
                id="columns-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                transformOrigin={{vertical: 'top', horizontal: 'center'}}
                PaperProps={{sx: {display: 'flex', flexDirection: 'column'}}}
            >
                <FormGroup sx={{px: 1, pt: 1, flexGrow: 1, overflowY: 'auto'}}>
                    {props.availableColumns.map((name) => (
                        <FormControlLabel
                            key={name}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={props.columnsToDisplay.includes(name)}
                                    onChange={() => handleToggleColumn(name)}
                                />
                            }
                            label={name}
                        />
                    ))}
                </FormGroup>

                <Divider />

                <Box sx={{px: 1, pb: 1, maxHeight: '1rem'}}>
                    <Button
                        variant="text"
                        disableRipple
                        size="small"
                        onClick={handleReset}
                        sx={{
                            'color': ezprOrange,
                            'textTransform': 'none',
                            '&:hover': {backgroundColor: 'transparent'},
                        }}
                    >
                        Reset To Default
                    </Button>
                </Box>
            </Menu>
        </>
    );
}

ColumnsButton.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    columnsToDisplay: PropTypes.array,
    setColumnsToDisplay: PropTypes.func,
    availableColumns: PropTypes.array,
};

